import { Form, Modal, Input, Select, message, Button } from "antd";
import React, { useState } from "react";
import { HiOutlineUser, HiOutlineMail } from "react-icons/hi";
import { GiPhone, GiPadlock } from "react-icons/gi";
import { useGetUserData } from "../../../hooks/useGetUserData";
import drEasyApi from "../../../Api/Customapi";

export default function CreatePatientModal({ visible, onClose,isMobile }) {
  const [signupForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { data } = useGetUserData();
  const { Option } = Select;

  const handleSignup = async (values) => {
    setLoading(true);
    try {
      console.log("Starting registration process");

      const endpoint = `/api/${data.slug}/User/RegisterAsCenterUser`;
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        gender: values.gender,
        password: values.password,
        birthDay: `${values.dobYear}-${String(values.dobMonth).padStart(2, "0")}-${String(values.dobDay).padStart(2, "0")}`,
      };

      console.log("API endpoint:", endpoint);
      const response = await drEasyApi.post(endpoint, payload);
      console.log("API response:", response);

      message.success("Registration successful! Check email for verification.");
      onClose();

      signupForm.resetFields();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 400) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors?.[0] ||
          "";
        message.error(errorMessage);
      } else if (error.response?.data?.errors) {
        message.error(error.response.data.errors[0]);
      } else {
        message.error("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      title="Create Patient"
      footer={null}
      width={isMobile ? "95%" : "46%"}
      centered={isMobile}
    >
      <Form
        form={signupForm}
        layout="vertical"
        onFinish={handleSignup}
        className="space-y-4"
        size="large"
      >
        <div className="flex gap-4">
          <Form.Item
            name="firstName"
            label="First Name"
            className="flex-1 mb-0"
            rules={[
              { required: true, message: "First name is required" },
              { min: 2, message: "First name must be at least 2 characters" },
            ]}
          >
            <Input  placeholder="Enter first name" prefix={<HiOutlineUser />} />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            className="flex-1 mb-0"
            rules={[
              { required: true, message: "Last name is required" },
              { min: 2, message: "Last name must be at least 2 characters" },
            ]}
          >
            <Input placeholder="Enter last name" prefix={<HiOutlineUser />} />
          </Form.Item>
        </div>
        <div className="flex gap-4 my-0">
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input
              placeholder="Enter email address"
              prefix={<HiOutlineMail />}
            />
          </Form.Item>

          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[
              { required: true, message: "Phone number is required" },
              {
                pattern: /^[\+]?[0-9][\d]{0,15}$/,
                message: "Enter a valid phone number",
              },
            ]}
          >
            <Input placeholder="Enter phone number" prefix={<GiPhone />} />
          </Form.Item>
          <Form.Item
            name="gender"
            label="Gender"
            rules={[{ required: true, message: "Gender is required" }]}
          >
            <Select placeholder="Select gender">
              <Select.Option value={0}>Male</Select.Option>
              <Select.Option value={1}>Female</Select.Option>
            </Select>
          </Form.Item>
        </div>

        <div className="flex gap-4 my-0">
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Please create a password" },
              { min: 6, message: "Password must be at least 6 characters" },
              {
                pattern:  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*#?&.]{6,}$/,
                message: "Password must contain letters and numbers",
              },
            ]}
          >
            <Input.Password
              placeholder="Enter password"
              prefix={<GiPadlock />}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="Confirm password"
              prefix={<GiPadlock />}
            />
          </Form.Item>
        </div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Date of Birth
        </label>
        <div className="grid grid-cols-3 gap-4">
          <Form.Item
            name="dobDay"
            label="Day"
            rules={[{ required: true, message: "Day is required" }]}
          >
            <Select placeholder="Day">
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <Option key={day} value={day}>
                  {day}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="dobMonth"
            label="Month"
            rules={[{ required: true, message: "Month is required" }]}
          >
            <Select placeholder="Month">
              {[
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ].map((month, index) => (
                <Option key={index + 1} value={index + 1}>
                  {month}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="dobYear"
            label="Year"
            rules={[{ required: true, message: "Year is required" }]}
          >
            <Select placeholder="Year" showSearch>
              {Array.from(
                { length: 100 },
                (_, i) => new Date().getFullYear() - i,
              ).map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          className="w-full py-3 h-auto text-white text-lg font-semibold bg-primary hover:bg-primary_hover border-none"
        >
          {loading ? "Registering..." : "Create Account"}
        </Button>
      </Form>
    </Modal>
  );
}
